/**
 * Agent 项目状态管理 Store
 * 使用 Zustand 进行状态管理
 * 注意：项目列表从后端 API 获取，不持久化到 localStorage
 */

import { create } from 'zustand'
import { Node, Edge } from 'reactflow'
import { ProjectStatus } from '@/shared/types/enums'
import type { AgentProject } from '@/modules/agent/types/project'

// 初始节点（新项目默认）
const getDefaultNodes = (): Node[] => [
  {
    id: "entry",
    type: "entry",
    position: { x: 50, y: 200 },
    data: { label: "用户输入" },
  },
  {
    id: "output",
    type: "output",
    position: { x: 500, y: 200 },
    data: { label: "回复用户" },
  },
]

const getDefaultEdges = (): Edge[] => []

// 初始项目数据 - 从后端 API 获取，不使用硬编码数据
const initialProjects: AgentProject[] = []

// Store 类型
interface ProjectStore {
  projects: AgentProject[]
  currentProject: AgentProject | null
  
  // Actions
  setCurrentProject: (projectId: string) => void
  createProject: (project: Omit<AgentProject, 'id' | 'status' | 'created_at' | 'updated_at' | 'nodes' | 'edges' | 'api_key' | 'api_key_created_at' | 'owner_id' | 'current_version'>) => AgentProject
  addProject: (project: AgentProject) => void
  updateProject: (projectId: string, updates: Partial<AgentProject>) => void
  updateProjectNodes: (projectId: string, nodes: Node[], edges: Edge[]) => void
  deleteProject: (projectId: string) => void
  getAgentCount: (projectId: string) => number
}

export const useProjectStore = create<ProjectStore>()(
    (set, get) => ({
      projects: initialProjects,
      currentProject: null,

      setCurrentProject: (projectId: string) => {
        const project = get().projects.find(p => p.id === projectId)
        set({ currentProject: project || null })
      },

      createProject: (projectData) => {
        const now = new Date().toISOString()
        const newProject: AgentProject = {
          ...projectData,
          id: `proj_${Date.now()}`,
          status: ProjectStatus.DRAFT,
          api_key: `sk_proj_${Date.now()}`,
          api_key_created_at: now,
          owner_id: "usr_001", // TODO: 从 authStore 获取当前用户ID
          current_version: 1,
          created_at: now,
          updated_at: now,
          nodes: getDefaultNodes(),
          edges: getDefaultEdges(),
        }
        set(state => ({
          projects: [newProject, ...state.projects],
          currentProject: newProject,
        }))
        return newProject
      },

      addProject: (project) => {
        set(state => {
          // 检查项目是否已存在
          if (state.projects.find(p => p.id === project.id)) {
            return state // 如果已存在，不重复添加
          }
          return {
            projects: [project, ...state.projects],
            currentProject: state.currentProject?.id === project.id ? project : state.currentProject,
          }
        })
      },

      updateProject: (projectId, updates) => {
        const now = new Date().toISOString()
        set(state => ({
          projects: state.projects.map(p => 
            p.id === projectId ? { ...p, ...updates, updated_at: now } : p
          ),
          currentProject: state.currentProject?.id === projectId 
            ? { ...state.currentProject, ...updates, updated_at: now }
            : state.currentProject,
        }))
      },

      updateProjectNodes: (projectId, nodes, edges) => {
        console.log('💾 [ProjectStore] updateProjectNodes 被调用:')
        console.log('  projectId:', projectId)
        console.log('  nodesCount:', nodes.length)
        console.log('  edgesCount:', edges.length)
        console.log('  edges summary:', JSON.stringify(edges.map(e => ({ 
          id: e.id, 
          source: e.source, 
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
          hasMarkerEnd: !!e.markerEnd,
          hasStyle: !!e.style,
          markerEnd: e.markerEnd,
          style: e.style,
        })), null, 2))
        console.log('  fullEdges:', JSON.stringify(edges, null, 2))
        
        const now = new Date().toISOString()
        set(state => {
          const updatedProjects = state.projects.map(p => {
            if (p.id === projectId) {
              const updated = { ...p, nodes, edges, updated_at: now }
              console.log('💾 [ProjectStore] 更新项目:')
              console.log('  projectId:', projectId)
              console.log('  nodesCount:', updated.nodes.length)
              console.log('  edgesCount:', updated.edges.length)
              console.log('  edges summary:', JSON.stringify(updated.edges.map((e: Edge) => ({ 
                id: e.id, 
                source: e.source, 
                target: e.target,
                sourceHandle: e.sourceHandle,
                targetHandle: e.targetHandle,
                markerEnd: e.markerEnd,
                style: e.style,
              })), null, 2))
              console.log('  fullEdges:', JSON.stringify(updated.edges, null, 2))
              return updated
            }
            return p
          })
          
          const updatedCurrentProject = state.currentProject?.id === projectId 
            ? { ...state.currentProject, nodes, edges, updated_at: now }
            : state.currentProject
            
          if (updatedCurrentProject && updatedCurrentProject.id === projectId) {
            console.log('💾 [ProjectStore] 更新currentProject:')
            console.log('  projectId:', projectId)
            console.log('  nodesCount:', updatedCurrentProject.nodes?.length ?? 0)
            console.log('  edgesCount:', updatedCurrentProject.edges?.length ?? 0)
            console.log('  edges summary:', JSON.stringify((updatedCurrentProject.edges ?? []).map((e: Edge) => ({ 
              id: e.id, 
              source: e.source, 
              target: e.target,
              sourceHandle: e.sourceHandle,
              targetHandle: e.targetHandle,
              markerEnd: e.markerEnd,
              style: e.style,
            })), null, 2))
            console.log('  fullEdges:', JSON.stringify(updatedCurrentProject.edges, null, 2))
          }
          
          return {
            projects: updatedProjects,
            currentProject: updatedCurrentProject,
          }
        })
        
        console.log('✅ [ProjectStore] updateProjectNodes 完成')
      },

      deleteProject: (projectId) => {
        set(state => ({
          projects: state.projects.filter(p => p.id !== projectId),
          currentProject: state.currentProject?.id === projectId ? null : state.currentProject,
        }))
      },

      getAgentCount: (projectId) => {
        const project = get().projects.find(p => p.id === projectId)
        if (!project) return 0
        return project.nodes?.filter(n => n.type === 'agent').length || 0
      },
    })
)

